import { dataDiffTypes } from 'global/common/dataDiff'
import _ from 'lodash'
import { useCallback } from 'react'

export const useCleanData = () => {

  const cleanUpData = useCallback(({
    data,
    removeUnderscores = false,
    removeProps = [],
    arrayOptions = {
      dedupe: true,
      collapseSingles: false,
      objectKey: '_itemKey'
    }
  }) => {
    return cleanForFirebase(data, { removeUnderscores, removeProps, arrayOptions })
  }, [])

  const validateData = useCallback(({ data }) => {
    return validateTheData(data)
  }, [])

  return { cleanUpData, validateData }

}

const cleanForFirebase = (
  data,
  {
    removeUnderscores = false,
    removeProps = [],
    arrayOptions = {
      dedupe: true,
      collapseSingles: false,
      objectKey: '_itemKey'
    }
  } = {}
) => {
  const removePropsSet = new Set(removeProps)
  const seenArrays = new WeakSet()

  const isEmptyValue = (v) =>
    v === null ||
    v === undefined ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0) ||
    (_.isPlainObject(v) && _.isEmpty(v))

  const isInvalidNumber = (v) =>
    typeof v === 'number' && !isFinite(v)

  const cleanArray = (arr) => {
    const { dedupe, collapseSingles, objectKey } = arrayOptions

    let cleaned = arr
      .map(item => traverse(item))
      .filter(v => !isEmptyValue(v) && !isInvalidNumber(v))

    // dedupe
    if (dedupe) {
      const seen = new Set()

      cleaned = cleaned.filter(item => {
        const key = _.isPlainObject(item)
          ? item?.[objectKey] ?? JSON.stringify(item)
          : item

        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    }

    // collapse single-item arrays
    if (collapseSingles && cleaned.length === 1) {
      return cleaned[0]
    }

    return cleaned
  }

  const traverse = (val) => {
    if (isEmptyValue(val) || isInvalidNumber(val)) return undefined

    if (Array.isArray(val)) {
      if (seenArrays.has(val)) return undefined
      seenArrays.add(val)
      return cleanArray(val)
    }

    if (_.isPlainObject(val)) {
      return _.reduce(
        val,
        (acc, v, key) => {
          if (removeUnderscores && key.startsWith('_')) return acc
          if (removePropsSet.has(key)) return acc

          const cleaned = traverse(v)

          if (isEmptyValue(cleaned) || isInvalidNumber(cleaned)) return acc

          acc[key] = cleaned
          return acc
        },
        {}
      )
    }

    return val
  }

  const result = traverse(data)

  // final safety pass
  if (isEmptyValue(result)) return undefined

  return result
}

const validateTheData = (
  data,
  {
    ignoreUnderscoreProps = true,
    checkEmptyStrings = true,
    checkNulls = true,
    checkEmptyArrays = true,
    checkEmptyObjects = true,
    checkDuplicates = true,
    objectKey = '_itemKey'
  } = {}
) => {
  const issues = []

  const addIssue = (type, path, value) => {
    issues.push({ type, path, value })
  }

  const walk = (value, path = '') => {
    // null / undefined
    if (checkNulls && _.isNil(value)) {
      addIssue('NULL', path, value)
      return
    }

    // NaN / Infinity
    if (_.isNumber(value) && !_.isFinite(value)) {
      addIssue('INVALID_NUMBER', path, value)
      return
    }

    // empty string
    if (
      checkEmptyStrings &&
      _.isString(value) &&
      _.trim(value) === ''
    ) {
      addIssue('EMPTY_STRING', path, value)
      return
    }

    // arrays
    if (_.isArray(value)) {
      if (checkEmptyArrays && _.isEmpty(value)) {
        addIssue('EMPTY_ARRAY', path, value)
      }

      if (checkDuplicates && !_.isEmpty(value)) {
        const seen = new Set()

        _.forEach(value, item => {
          const dedupeKey = _.isPlainObject(item)
            ? _.get(item, objectKey) ?? JSON.stringify(item)
            : item

          if (seen.has(dedupeKey)) {
            addIssue(
              'DUPLICATE_ARRAY_ITEM',
              path,
              dedupeKey
            )
          }

          seen.add(dedupeKey)
        })
      }

      _.forEach(value, (item, index) => {
        walk(item, `${path}[${index}]`)
      })

      return
    }

    // objects
    if (_.isPlainObject(value)) {
      if (checkEmptyObjects && _.isEmpty(value)) {
        addIssue('EMPTY_OBJECT', path, value)
        return
      }

      _.forOwn(value, (child, key) => {
        if (
          ignoreUnderscoreProps &&
          _.startsWith(key, '_')
        ) {
          return
        }

        walk(
          child,
          path ? `${path}.${key}` : key
        )
      })

      return
    }
  }

  walk(data)

  return {
    valid: _.isEmpty(issues),
    issueCount: _.size(issues),
    summary: _.countBy(issues, 'type'),
    issues
  }
}